import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { ErrorDialog, InfoDialog, ConfirmDialog } from "Reuse/dialog";
import i18n from "i18n";

/**
 * DialogContext
 * -------------
 * Provides a global mechanism to show/hide error, info, and confirm dialogs.
 */
const DialogContext = createContext();

export const DialogProvider = ({ children }) => {
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // Show confirm dialog
  // Stable functions (state setters only), so hooks can list them as deps
  const showConfirm = useCallback((message = null, note = null, close = null, cancel = null) => {
    setConfirm({ message, note, close, cancel });
  }, []);
  const hideConfirm = useCallback(() => setConfirm(null), []);

  // Show error dialog
  const showError = useCallback((message, close = null) => setError({ message, close }), []);
  const hideError = useCallback(() => setError(null), []);

  // Show info dialog
  const showInfo = useCallback(
    ({ message, title = i18n.t("common:info"), close = null }) => setInfo({ message, close, title }),
    [],
  );
  const hideInfo = useCallback(() => setInfo(null), []);

  const value = useMemo(
    () => ({
      error,
      showError,
      hideError,
      info,
      showInfo,
      hideInfo,
      confirm,
      showConfirm,
      hideConfirm,
    }),
    [error, info, confirm, showError, hideError, showInfo, hideInfo, showConfirm, hideConfirm]
  );

  return (
    <DialogContext.Provider value={value}>
      <ErrorDialog />
      <InfoDialog />
      <ConfirmDialog />
      {children}
    </DialogContext.Provider>
  );
};

export const useDialog = () => useContext(DialogContext);
